import { useDispatch, useSelector } from 'react-redux';
import { MapPinIcon } from '../../../../utils/svg';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { handleOutletByLocation, setOutletDetail } from '../../../../store/reducers/outlet';
import OtherLocation from '../OtherLocation';
import { BottomSheet } from 'react-spring-bottom-sheet';
import { useCallback, useEffect, useRef, useState } from 'react';
import useNavigation from '../../../../hook/useNavigation';
import { UFOODX_BASE_URL } from '../../../../utils/constants';
import { useParams } from 'react-router-dom';
import { removeCart } from '../../../../store/reducers/cart';
import { authHeaders, axiosInstance, isEmptyData } from '../../../../utils/helper';
import { useLocation } from "react-router-dom";
import { tableNumberAuto } from '../../../../store/reducers/order';

const Confirmation = ({ onConfirm, onCancel }) => {
	return (
		<div className={`bg-white p-4 shadow-custom transition-transform duration-1000 ease-in-out rounded-t-xl translate-y-0 relative !z-[7]`}>
			<div className='pt-4 text-base text-gray-900 font-semibold'>Apakah Anda yakin ingin mengganti outlet?</div>
			<div className='pt-1 text-xxs text-gray-900 font-normal'>Jika Anda meninggalkan halaman ini, makanan atau meja yang telah Anda pilih akan hilang.</div>
			<div className='flex gap-2 mt-10'>
				<button className='w-full bg-[var(--rose-violet)] text-white flex justify-center font-normal text-sm py-2 px-3 border-0 items-center' onClick={onConfirm}>
					Ya
				</button>
				<button className='w-full bg-[var(--rose-violet)] text-white flex justify-center font-normal text-sm py-2 px-3 border-0 items-center' onClick={onCancel}>
					Tidak
				</button>
			</div>
		</div>
	);
};

const Address = () => {
	const { type } = useParams();
	const dispatch = useDispatch();
	const navigate = useNavigation();
	const location = useLocation();
	const user = useSelector((state) => state.user);
	const { details: outlet } = useSelector((store) => store?.outlet);
	const [isShowBottomSheet, setIsShowBottomSheet] = useState(false);
	const [isShowConfirmation, setIsShowConfirmation] = useState(false);
	const [selectedOutlet, setSelectedOutlet] = useState(null);
	const company = useSelector((state) => state.company);

	const isFirstRender = useRef(true);
	const isRoot = new RegExp(`^${UFOODX_BASE_URL}/?$`).test(location.pathname);

	const handleClick = async () => {
		await dispatch(
			handleOutletByLocation({
				company_id: company?.companyId,
				item: {
					brandID: outlet?.brand_id,
          companyID: company?.companyID,
				},
				latitude: user?.latitude,
				longitude: user?.longitude,
				page: 1,
				limit: 10,
				isLoadMore: false,
			}),
		).unwrap();
		setIsShowBottomSheet(true);
	};

	const handleCloseBottomSheet = () => {
		setIsShowBottomSheet(false);
		setIsShowConfirmation(false);
		setSelectedOutlet(null);
	};

	const handleConfirm = () => {
		dispatch(
			removeCart({
				customerID: user?.customerID,
			}),
		).unwrap();

		const currentPath = window.location.pathname;
		const segments = currentPath.split('/').filter(Boolean);
		if (segments.length > 1) {
			navigate.push(`${UFOODX_BASE_URL}/${selectedOutlet?.storeSlug}/${type}`);
		}

		dispatch(
			setOutletDetail({
				...selectedOutlet,
				brand_id: selectedOutlet?.brandID,
			}),
		);

		if (type === 'dinein') {
			dispatch(
				tableNumberAuto({
					companyId: Number(company?.companyID),
					outletId: Number(selectedOutlet?.storeID),
					brandId: Number(selectedOutlet?.brandID),
				})
			);
		}
		handleCloseBottomSheet();
	};

	const handleCancel = () => {
		handleCloseBottomSheet();
	};

	const handleSelectOutlet = (outlet) => {
		setSelectedOutlet(outlet);
		setIsShowConfirmation(true);
	};

	const handleGetNearestOutlet = useCallback(async () => {
		console.log('handleGetNearestOutlet');
		const companydID = user?.companyID || company?.companyID;
		const brandID = outlet?.brand_id || company?.brandId;
		const url = `store/listStore?brand_id=${brandID}&latitude=${user?.latitude}&longitude=${user?.longitude}&page=1&limit=10&company_id=${companydID}`;

		const response = await axiosInstance.get(url, authHeaders());
		if (response?.data?.status === 'ok') {
			const result = response?.data?.result || [];
			const nearst = result?.[0];
			if (!isRoot) !isEmptyData(nearst) && navigate.push(`${UFOODX_BASE_URL}/${nearst?.storeSlug}/${type}`);
		}
	}, [user?.address, isRoot]);

	useEffect(() => {
		if (isFirstRender.current) {
			isFirstRender.current = false;
			return;
		}

		handleGetNearestOutlet();
	}, [handleGetNearestOutlet]);

	const distance = parseFloat(outlet?.distance || 0)?.toFixed('2');

	return (
		<>
			<div className='flex py-2 items-center px-4'>
				<div className='rounded-full bg-fuchsia-500/20'>
					<MapPinIcon className='h-5 w-auto text-fuchsia-700/60 m-1' />
				</div>
				<div className='pl-2 flex items-center w-full justify-between' onClick={handleClick}>
					<div>
						<div className='font-normal text-xxs text-gray-500'>Nearest to you</div>
						<div className='font-semibold text-xs text-gray-700'>{outlet?.storeName}</div>
						<div className='items-center flex'>
							<div className='font-normal text-xxs text-gray-500 shrink-0 flex items-center'>
								<ChevronLeftIcon className='h-2 w-auto text-gray-700' /> {distance} Km
							</div>
							<div className='h-1 w-1 bg-gray-400 rounded-full mx-1 shrink-0' />
							<div className='font-normal text-xxs text-gray-500 line-clamp-1 min-w-[calc(80%-100px)]'>{outlet?.storeAddress}</div>
						</div>
					</div>
					<div>
						<ChevronRightIcon className='h-3.5 sm:h-5 w-auto text-gray-700 text-xxs' strokeWidth={3} />
					</div>
				</div>
			</div>
			<BottomSheet
				key={isShowConfirmation ? 'confirmation' : 'other'}
				open={isShowBottomSheet}
				onDismiss={handleCloseBottomSheet}
				snapPoints={({ minHeight, maxHeight }) => {
					return [minHeight, maxHeight * 0.75];
				}}>
				{isShowConfirmation ? <Confirmation onConfirm={handleConfirm} onCancel={handleCancel} /> : <OtherLocation onSelect={handleSelectOutlet} />}
			</BottomSheet>
		</>
	);
};

export default Address;
